import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import {
  FieldPathByValue,
  FieldValues,
  get,
  useFormContext,
} from 'react-hook-form';

type FormNumberInputProps<FormInputs extends FieldValues> = {
  inputPath: FieldPathByValue<FormInputs, number | null>;
  required?: boolean;
  placeholder: string;
  label?: string;
  helperText?: string;
  suffix?: string;
  sx?: FormControlProps;
};

export function FormNumberInput<FormInputs extends FieldValues>({
  inputPath,
  required,
  placeholder,
  label,
  helperText,
  suffix,
  sx,
}: FormNumberInputProps<FormInputs>) {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext<FormInputs>();

  const error = get(errors, inputPath);

  const value = watch(inputPath);

  return (
    <FormControl isInvalid={!!error} {...sx}>
      {label && (
        <FormLabel
          htmlFor={inputPath}
          fontSize="16px"
          fontWeight={500}
          color="content.title"
          mb={3}>
          {label}
        </FormLabel>
      )}
      <InputGroup>
        <Input
          bg="surface.panel"
          border="1px solid"
          borderColor="surface.border"
          borderRadius="md"
          py={3}
          px={4}
          fontSize="16px"
          _placeholder={{ color: 'content.muted' }}
          _focus={{
            borderColor: 'icon.green',
            boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
          }}
          {...register(inputPath, {
            required: required ? 'Required' : false,
            valueAsNumber: true,
          })}
          type={'number'}
          id={inputPath}
          value={value ?? ''}
          placeholder={placeholder}
        />
        {suffix && <InputRightElement>{suffix}</InputRightElement>}
      </InputGroup>
      {error?.message && typeof error.message === 'string' && (
        <FormErrorMessage>{error.message}</FormErrorMessage>
      )}
      {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
